/**
 * UTC calendar helpers for the learn rules. Every due date, week and retake
 * in the Academy is a UTC day (the scenarios pin it, as notes already do), so
 * the device and the edge agree whatever timezone either one sits in.
 */

const DAY_MS = 86_400_000;
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?Z$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Milliseconds since the epoch, or `NaN` when `iso` is not a date the engine can read. */
export function epochMs(iso: string): number {
  return Date.parse(iso);
}

/** `true` for an ISO 8601 UTC instant (`…Z`), the only spelling progress items carry. */
export function isIsoInstant(value: unknown): value is string {
  return typeof value === "string" && INSTANT.test(value) && !Number.isNaN(Date.parse(value));
}

/** `true` for a real `YYYY-MM-DD` calendar day (so `2026-02-30` is not one). */
export function isUtcDay(value: unknown): value is string {
  if (typeof value !== "string" || !DAY.test(value)) return false;
  const at = new Date(`${value}T00:00:00Z`);
  // An impossible day such as 2026-02-30 is an invalid Date, which has no ISO string to compare.
  return !Number.isNaN(at.getTime()) && at.toISOString().startsWith(value);
}

/** A clock must be a real instant: scheduling against `NaN` would corrupt every due date. */
export function toDate(input: Date | string): Date {
  const date = typeof input === "string" ? new Date(input) : new Date(input.getTime());
  if (Number.isNaN(date.getTime())) throw new RangeError("the clock is not a valid instant");
  return date;
}

/** `YYYY-MM-DD` of the UTC day containing `input`. */
export function utcDay(input: Date | string): string {
  return toDate(input).toISOString().slice(0, 10);
}

/** 00:00:00.000Z of the UTC day containing `input`. */
export function startOfUtcDay(input: Date | string): Date {
  return new Date(`${utcDay(input)}T00:00:00.000Z`);
}

/** The day `days` after `day` (negative goes back); `day` must be a valid `YYYY-MM-DD`. */
export function addUtcDays(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Whole UTC days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

/** The instant the next UTC day starts: a failed sitting reopens exactly then. */
export function nextUtcMidnight(input: Date | string): string {
  return startOfUtcDay(new Date(startOfUtcDay(input).getTime() + DAY_MS)).toISOString();
}
